import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { enablePush, pushSupported, isIOSDevice, isStandalonePWA } from "../lib/push";
import { toast } from "sonner";

const KEY = "haplap_notify_prompt_v1";

export default function NotifyPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !pushSupported()) return;
    if (isIOSDevice() && !isStandalonePWA()) return; // iOS push needs the installed PWA
    if (Notification.permission !== "default") return; // already granted or denied
    try { if (localStorage.getItem(KEY)) return; } catch { /* ignore */ }
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await enablePush();
      if (ok) toast.success("Notifications on 💛 You'll hear the moment someone helps you.");
      else toast.message("No worries — you can turn them on anytime from the menu.");
    } catch (e) {
      toast.error("Couldn't enable notifications on this device.");
    }
    setBusy(false);
    dismiss();
  };

  if (!show) return null;

  return (
    <div data-testid="notify-prompt"
      className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] md:bottom-4 z-[75] px-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md bg-white border border-penny/30 rounded-2xl shadow-xl p-3.5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-penny/15 text-penny flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p className="font-serif font-semibold text-bronze text-sm leading-tight">Turn on notifications</p>
          <p className="text-xs text-muted-foreground leading-snug">Hear the moment someone tips or thanks you 💛</p>
        </div>
        <Button data-testid="notify-enable-btn" size="sm" onClick={enable} disabled={busy}
          className="rounded-full bg-copper hover:bg-copper/90 text-white shrink-0">
          {busy ? "…" : "Enable"}
        </Button>
        <button data-testid="notify-dismiss-btn" onClick={dismiss}
          className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground shrink-0" aria-label="Not now">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
